import React, { useState, useMemo } from 'react';
import { ReactComponent as BtnDislikeFill } from '../../../assets/svg/disliked_old.svg';
import { ReactComponent as BtnDislike } from '../../../assets/svg/dislike_old.svg';
import { ReactComponent as BtnLikeFill } from '../../../assets/svg/like_fill_v2.svg';
import { ReactComponent as BtnLike } from '../../../assets/svg/like_v2.svg';
import { ReactionType, STRINGS } from '../../../constants';
import { useAuthorize } from 'hooks/useAuthorize';
import { TOGGLE_REACTION } from 'graph_ql/reaction/mutations';
import { FEEDBACK_TRIGGER_TYPE } from 'component/style_item/type';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { GET_FEEDBACK_OPTIONS } from 'graph_ql/feedback_options/queries';
import { FeedbackReason } from 'graph_ql/feedback/types';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import UserType from 'constants/user';
import MultiSelectPopup from 'component/MultiSelectPopup';
import { DEFAULT_FEEDBACK_OPTION, Options } from 'component/MultiSelectPopup/types';
import { CREATE_FEEDBACK } from 'graph_ql/feedback/mutations';
import { GET_REACTION_INFORMATION } from 'graph_ql/reaction/queries';
import utils from 'utils/utils';
import styleItemUtils from 'utils/style_item';
import useUnlockExpProcessing from '../hooks/useUnlockExpProcessing';

interface ReactionActionsProps {
    productDetail: {
        collectionName: string;
        productId: string;
        productType: string;
        productTaffiId?: string;
    };
    customAnalyticsData?: any;
    className?: string;
    onReactionClick?: (action: ReactionType) => void;
}

const ReactionActions: React.FC<ReactionActionsProps> = ({
    productDetail,
    customAnalyticsData = { recommendedBy: STRINGS.STYLE_ASSISTANT },
    className,
    onReactionClick,
}) => {
    const { openAuthPopup } = useUnlockExpProcessing();
    const authorize = useAuthorize();
    const userDetail = useSelector((state: RootState) => state.userDetail.me);
    const [feedbackOptions, setFeedbackOptions] = useState<FeedbackReason[]>([]);
    const [feedbackPopupShow, setFeedbackPopupShow] = useState<boolean>(false);
    const [customValueError, setCustomValueError] = useState<boolean>(false);
    const [reaction, setReaction] = useState({ like: false, dislike: false, totalLike: 0, totalDislike: 0 });
    const isCustomer = Boolean(userDetail && userDetail.userType === UserType.Customer);
    const [selectedFeedbacks, setSelectedFeedbacks] = useState<Options>(DEFAULT_FEEDBACK_OPTION);

    const reactionActions = useMemo(
        () => [
            {
                action: ReactionType.Like,
                component: reaction.like ? BtnLikeFill : BtnLike,
            },
            {
                action: ReactionType.Dislike,
                component: reaction?.dislike ? BtnDislikeFill : BtnDislike,
            },
        ],
        [reaction],
    );

    const [toggleReactionMutation] = useMutation(TOGGLE_REACTION, {
        ...utils.gqlMutationOption({}),
        context: { allowAnonymousAuth: utils.isLocalUser() },
    });

    const handleOnCompleted = (response) => {
        setFeedbackOptions(response?.getFeedbackOptions);
    };

    const [getFeedbackOptions, { loading: getFeedbackOptionsLoading }] = useLazyQuery(
        GET_FEEDBACK_OPTIONS,
        utils.gqlQueryOption({
            fetchPolicy: 'network-only',
            onCompleted: handleOnCompleted,
        }),
    );

    const [createFeedback, { loading: createFeedbackLoading }] = useMutation(CREATE_FEEDBACK);

    const defaultApiVariables = useMemo(() => {
        return {
            collectionName: productDetail.collectionName,
            docId: productDetail?.productId,
        };
    }, [productDetail]);

    const openFeedBack = () => {
        setFeedbackPopupShow(true);
    };

    useQuery(GET_REACTION_INFORMATION, {
        ...utils.gqlQueryOption({
            variables: { ...defaultApiVariables },
            fetchPolicy: 'cache-and-network',
            skip: !defaultApiVariables.docId,
            onCompleted: (response) =>
                styleItemUtils.updateReactionFromResp(response?.getReactionInformation, setReaction),
        }),
        context: { allowAnonymousAuth: utils.isLocalUser() },
    });

    const handleCustomValueError = (state: boolean) => {
        setCustomValueError(state);
    };

    const submitFeedback = async () => {
        styleItemUtils.handleSubmitFeedback({
            selectedFeedbacks,
            handleCustomValueError,
            createFeedback,
            productDetail: productDetail as any,
        });
    };

    const handleCloseMultiSelectPopup = () => {
        setFeedbackPopupShow(false);
    };

    const customAuthorize = () => {
        openAuthPopup();
    };

    const handleReactionClick = async (action: ReactionType) => {
        if (onReactionClick) {
            onReactionClick(action);
        }

        styleItemUtils.handleReaction({
            authorize,
            customAuthorize,
            userDetail,
            productDetail: productDetail as any,
            customAnalyticsData,
            action,
            isCustomer,
            reaction,
            setReaction,
            toggleReactionMutation,
            getFeedbackOptions,
            openFeedBack,
            feedbackType: FEEDBACK_TRIGGER_TYPE.DISLIKE,
        });
    };

    return (
        <>
            <div className={`taffi-reaction-actions ${className || ''}`}>
                {reactionActions.map((item, index) => {
                    return (
                        <div
                            key={index}
                            className="taffi-reaction-action"
                            style={{ marginLeft: index > 0 ? '4px' : '0' }}
                            onClick={() => handleReactionClick(item.action)}
                        >
                            <item.component className="taffi-reaction-icon" />
                        </div>
                    );
                })}
            </div>
            <MultiSelectPopup
                show={feedbackPopupShow}
                onClose={handleCloseMultiSelectPopup}
                title={STRINGS.ADD_FEEDBACK}
                options={feedbackOptions}
                selectedOptions={selectedFeedbacks}
                setSelectedOptions={setSelectedFeedbacks}
                loading={getFeedbackOptionsLoading}
                handleSubmit={submitFeedback}
                customValueError={customValueError}
                setCustomValueError={setCustomValueError}
                setHidePopup={setFeedbackPopupShow}
                successMessage={STRINGS.FEEDBACK_RECEIVED}
                successDetails={STRINGS.FEEDBACK_SUCCESS_MESSAGE}
                submitLoading={createFeedbackLoading}
            />
        </>
    );
};

export default ReactionActions;
